export interface LoopUiData {
  loop?: boolean;
  loopStep?: boolean;
  loopEnd?: boolean;
  loopStartOrigId?: string;
  loopStepOrigId?: string;
  loopEndOrigId?: string;
  steps?: Array<{ loopStepIndex: number; origId: string }>;
}

export interface RouterUiData {
  insideLoop?: boolean;
  parentLoopOrigId?: string;
  loopEndOrigId?: string;
}

export type StepUiData = LoopUiData & RouterUiData;

/**
 * Safely parses uiData JSON string into a typed object.
 * Returns null if parsing fails or uiData is undefined/null.
 */
export function parseUiData<T = StepUiData>(
  uiData: string | undefined | null
): T | null {
  if (!uiData) return null;

  try {
    return JSON.parse(uiData) as T;
  } catch (e) {
    console.warn("Failed to parse uiData:", e);
    return null;
  }
}

/**
 * Safely stringifies uiData object to JSON string.
 */
export function stringifyUiData(data: StepUiData): string {
  return JSON.stringify(data);
}

/**
 * Creates uiData for a loop start step.
 */
export function createLoopStartUiData(
  loopEndOrigId: string,
  steps: Array<{ loopStepIndex: number; origId: string }> = [],
  parentLoopOrigId?: string
): string {
  const data: StepUiData = {
    loop: true,
    loopEndOrigId,
    steps,
  };

  if (parentLoopOrigId) {
    data.loopStep = true;
    data.loopStartOrigId = parentLoopOrigId;
  }

  return stringifyUiData(data);
}

/**
 * Creates uiData for a step inside a loop.
 */
export function createLoopStepUiData(loopStartOrigId: string): string {
  const data: StepUiData = {
    loopStep: true,
    loopStartOrigId,
  };

  return stringifyUiData(data);
}

/**
 * Creates uiData for a loop end step.
 */
export function createLoopEndUiData(loopStartOrigId: string): string {
  return stringifyUiData({
    loopEnd: true,
    loopStartOrigId,
  });
}

/**
 * Creates uiData for a router inside a loop.
 */
export function createRouterInsideLoopUiData(
  parentLoopOrigId: string,
  loopEndOrigId: string
): string {
  return stringifyUiData({
    insideLoop: true,
    parentLoopOrigId,
    loopEndOrigId,
  });
}

/**
 * Updates the steps array in a loop's uiData.
 * Returns the updated uiData string.
 */
export function updateLoopStepsInUiData(
  currentUiData: string | undefined,
  stepOrigId: string,
  index: number,
  operation: "add" | "remove"
): string {
  const uiData = parseUiData(currentUiData) ?? { loop: true, steps: [] };

  if (!uiData.steps) {
    uiData.steps = [];
  }

  if (operation === "add") {
    const exists = uiData.steps.some((s) => s.origId === stepOrigId);
    if (!exists) {
      uiData.steps.splice(index, 0, {
        loopStepIndex: index,
        origId: stepOrigId,
      });
    }
  } else if (operation === "remove") {
    uiData.steps.splice(index, 1);
  }

  // Re-index all steps
  uiData.steps.forEach((step, idx) => {
    step.loopStepIndex = idx;
  });

  return stringifyUiData(uiData);
}

/**
 * Merges new properties into existing uiData.
 */
export function mergeUiData(
  currentUiData: string | undefined,
  newData: Partial<StepUiData>
): string {
  const existing = parseUiData(currentUiData) ?? {};
  return stringifyUiData({ ...existing, ...newData });
}
